import React, { FC, useEffect, useMemo, useState, useRef } from "react";
import { Input, Select, Modal, Button } from "antd";
import "./VoiceLibrary.scss";
import { MiniPaginator, CopyrightFooter, AlertMessage } from "../../components/common";
import type { AlertConfig } from "../../components/common";
import { getSeriesSpeakersApi, createSpeakerApi, deleteSpeakerApi, demoteSpeakerSubtitleApi, getSpeakersSeriesApi, queryVoiceToneByRoleAndLanguageApi } from "../../service/speakers";
import { getSeriesListApi } from "../../service/series";
import { ALERT_Z_INDEX } from "../../utils/alertManager";
import { VIDEO_CDN_PREFIX } from "../../config/video";
import { getLanguageName } from "../../utils/languageUtils";
import { UserRoleManager } from "../../utils/userRole";
import { globalStore } from "../../utils/globalStore";

interface VoiceItem {
  id: number;
  name: string;
  characterCount: number;
  isNew?: boolean;
}

interface LanguageItem {
  seriesId: number;
  name: string;
  langCode: string;
  speakerCount: number;
}

export const VoiceLibrary: FC = () => {
  const [searchQuery, setSearchQuery] = useState(''); // 用于实际查询（防抖后更新）
  const [searchInputValue, setSearchInputValue] = useState(''); // 用于输入框显示（立即更新）
  const [sortBy, setSortBy] = useState<'time' | 'alphabet'>('time');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null); // 防抖定时器

  // 弹窗相关状态
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [selectedSeriesId, setSelectedSeriesId] = useState<number | null>(null);
  const [selectedSeriesName, setSelectedSeriesName] = useState<string>('');
  const [selectedLangCode, setSelectedLangCode] = useState<string>(''); // 当前选中的语言代码
  const [seriesSpeakers, setSeriesSpeakers] = useState<Speakers.Speaker[]>([]);
  const [selectedSpeakerId, setSelectedSpeakerId] = useState<number | null>(null);
  const [selectedSpeakerName, setSelectedSpeakerName] = useState<string>('');
  const [speakerSubtitles, setSpeakerSubtitles] = useState<any[]>([]);
  const [loadingSpeakers, setLoadingSpeakers] = useState(false);
  const [loadingSubtitles, setLoadingSubtitles] = useState(false);
  const [playingClipId, setPlayingClipId] = useState<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [alertConfig, setAlertConfig] = useState<AlertConfig | null>(null);
  const CDN_PREFIX = VIDEO_CDN_PREFIX;
  
  // 新建角色相关状态
  const [isCreatingRole, setIsCreatingRole] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');

  // 内层语言列表相关状态
  const [isInLanguageList, setIsInLanguageList] = useState(false); // 是否在内层语言列表
  const [currentSeriesInfo, setCurrentSeriesInfo] = useState<{ id: number; name: string } | null>(null); // 当前选中的剧集信息
  const [languageListData, setLanguageListData] = useState<LanguageItem[]>([]); // 语言列表数据
  const [loadingLanguages, setLoadingLanguages] = useState(false); // 加载语言列表状态
  const [languagePage, setLanguagePage] = useState(1); // 内层语言列表分页
  const [languagePageSize] = useState(20); // 内层语言列表每页数量

  // 检查是否是管理员或超级管理员
  const isAdminOrSuperAdmin = (): boolean => {
    const currentRole = UserRoleManager.getCurrentUserRole();
    if (currentRole?.isAdmin) {
      return true;
    }
    // 额外检查：直接从用户信息中检查角色key
    const userInfo = globalStore.getUserInfo();
    if (userInfo?.roles) {
      const roleKeys = userInfo.roles.map((role: any) => 
        (typeof role === 'string' ? role : role.role_key || role.code || '').toLowerCase()
      );
      return roleKeys.some((key: string) => key === 'super_admin' || key === 'admin' || key === 'administrator');
    }
    return false;
  };

  // 根据用户角色设置默认的 ownerType：普通用户默认为2（普通用户剧集），其他角色默认为1（团队剧集）
  const getDefaultOwnerType = (): number => {
    const currentRole = UserRoleManager.getCurrentUserRole();
    if (currentRole?.isUser) {
      return 2; // 普通用户默认显示普通用户剧集
    }
    return 1; // 其他角色默认显示团队剧集
  };

  const [ownerType, setOwnerType] = useState<number>(getDefaultOwnerType()); // 剧集拥有者类型：1代表团队，2代表普通用户剧集

  // 根据用户角色初始化 ownerType（仅在组件挂载时执行一次）
  useEffect(() => {
    const currentRole = UserRoleManager.getCurrentUserRole();
    if (currentRole?.isUser) {
      setOwnerType(2); // 普通用户设置为普通用户剧集
    }
    // 非普通用户保持默认值1（团队剧集），不需要更新
  }, []); // 空依赖数组，仅在组件挂载时执行一次

  const [apiData, setApiData] = useState<Speakers.SpeakersSeriesListResponse>({
    data: [],
    pagination: {
      page: 1,
      per_page: pageSize,
      total: 0,
      pages: 1,
      has_next: false,
      has_prev: false,
      sort: 'latest'
    }
  });

  // 拉取音色库剧集列表
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // 构建请求参数
        const params: Series.GetSeriesListParams = {
          pageNo: page,
          pageSize: pageSize,
          ownerType: ownerType || 1, // 剧集拥有者类型：1代表团队，2代表普通用户剧集，默认值为1
        };
        
        // 排序参数：1.时间 2.名称
        const sortMap: Record<'time' | 'alphabet', number> = {
          'time': 1,      // 按时间排序 -> 1.时间
          'alphabet': 2,  // 按名称排序 -> 2.名称
        };
        params.sort = sortMap[sortBy] || 1;
        
        // 如果有搜索关键词，添加 name 参数
        if (searchQuery.trim()) {
          params.name = searchQuery.trim();
        }
        
        const res = await getSeriesListApi(params);
        
        // 转换新接口响应格式为旧格式（保持兼容性）
        // 响应格式：{ code: 0, data: { list: [{ seriesId, name, languageCount, ... }], total }, msg: "" }
        const list = res.data?.list || [];
        const total = res.data?.total || 0;
        
        setApiData({
          data: list.map(item => ({
            series_id: item.seriesId || item.id || 0, // 优先使用 seriesId，兼容 id 字段
            series_name: item.name,
            total: item.languageCount || 0
          })),
          pagination: {
            page: page,
            per_page: pageSize,
            total: total,
            pages: Math.max(1, Math.ceil(total / pageSize || 1)),
            has_next: page * pageSize < total,
            has_prev: page > 1,
            sort: 'latest'
          }
        });
      } catch (err) {
        setAlertConfig({ type: 'error', message: '获取音色库数据失败' });
        setApiData(prev => ({
          data: [],
          pagination: { ...prev.pagination, page: 1, pages: 1, total: 0, has_next: false, has_prev: false }
        }));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [page, pageSize, ownerType, searchQuery, sortBy]);

  // 过滤和排序数据
  // 将接口数据映射为卡片展示数据（series_id -> id, series_name -> name, total -> characterCount）
  const apiVoiceItems: VoiceItem[] = useMemo(() => {
    return (apiData.data || []).map((item: Speakers.SpeakersSeriesItem) => ({
      id: item.series_id,
      name: item.series_name,
      characterCount: item.total,
    }));
  }, [apiData]);

  // 语言列表数据（内层）- 过滤
  const filteredLanguageData = useMemo(() => {
    if (!isInLanguageList) return [];
    return languageListData.filter(item =>
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      getLanguageName(item.langCode).toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [languageListData, searchQuery, isInLanguageList]);

  // 内层语言列表分页数据
  const pagedLanguageData = useMemo(() => {
    if (!isInLanguageList) return [];
    const start = (languagePage - 1) * languagePageSize;
    const end = start + languagePageSize;
    return filteredLanguageData.slice(start, end);
  }, [filteredLanguageData, languagePage, languagePageSize, isInLanguageList]);

  // 内层语言列表分页信息
  const languagePages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredLanguageData.length / languagePageSize));
  }, [filteredLanguageData.length, languagePageSize]);

  const languageHasPrev = languagePage > 1;
  const languageHasNext = languagePage < languagePages;

  // 直接使用后端返回的数据（后端已处理搜索、排序和分页）
  const pages = apiData.pagination.pages;
  const hasPrev = apiData.pagination.has_prev;
  const hasNext = apiData.pagination.has_next;
  const pagedData = apiVoiceItems; // 当前页数据（后端已处理）

  const handleSortChange = (value: 'time' | 'alphabet') => {
    setSortBy(value);
    setPage(1); // 切换排序时重置到第一页
    // 如果在内层，重置内层分页
    if (isInLanguageList) {
      setLanguagePage(1);
    }
  };

  // 搜索输入框变化处理（带防抖）
  const handleSearchChange = (value: string) => {
    setSearchInputValue(value); // 立即更新输入框显示
    
    // 清除之前的定时器
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // 设置新的防抖定时器，延迟更新 searchQuery 触发API调用
    debounceTimerRef.current = setTimeout(() => {
      setSearchQuery(value);
      setPage(1); // 搜索时重置到第一页
      debounceTimerRef.current = null;
    }, 1000); // 防抖延迟1秒
  };

  // 组件卸载时清除定时器
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // 同步 searchQuery 到 searchInputValue（当 searchQuery 从外部更新时）
  useEffect(() => {
    if (!debounceTimerRef.current) {
      setSearchInputValue(searchQuery);
    }
  }, [searchQuery]);

  // 当搜索改变时，如果在内层，重置内层分页
  useEffect(() => {
    if (isInLanguageList) {
      setLanguagePage(1);
    }
  }, [searchQuery, isInLanguageList]);

  // 真实数据：不再使用假数据回退

  // 点击音色卡片，获取该剧集的语言列表
  const handleCardClick = async (seriesId: number, seriesName: string) => {
    try {
      setLoadingLanguages(true);
      setCurrentSeriesInfo({ id: seriesId, name: seriesName });
      
      // 调用 getSpeakersSeriesApi 获取该剧集的语言列表
      const response = await getSpeakersSeriesApi(seriesId);
      
      if (response.code === 0 && response.data) {
        // 转换数据格式
        const languages: LanguageItem[] = response.data.map(item => ({
          seriesId: item.seriesId,
          name: item.name,
          langCode: item.langCode,
          speakerCount: item.speakerCount
        }));
        
        setLanguageListData(languages);
        setIsInLanguageList(true); // 进入内层语言列表
      } else {
        setAlertConfig({ type: 'error', message: '获取语言列表失败' });
      }
    } catch (error) {
      console.error('获取语言列表失败:', error);
      setAlertConfig({ type: 'error', message: '获取语言列表失败' });
    } finally {
      setLoadingLanguages(false);
    }
  };

  // 返回外层剧集列表
  const handleBackToSeriesList = () => {
    setIsInLanguageList(false);
    setCurrentSeriesInfo(null);
    setLanguageListData([]);
    // 重置搜索和排序
    setSearchQuery('');
    setSortBy('time');
    setPage(1);
    // 重置内层分页
    setLanguagePage(1);
  };

  // 加载指定语言的说话人列表
  const loadSpeakersForLanguage = async (seriesId: number, langCode: string) => {
    try {
      setLoadingSpeakers(true);
      const response = await getSeriesSpeakersApi(seriesId, langCode);
      const speakers = Array.isArray(response) ? response : (response.data || []);
      setSeriesSpeakers(speakers);

      // 默认选中第一个角色，并通过 /video/voice-tone/query-by-role-and-language 获取该角色的字幕片段
      const defaultSpeaker = speakers.length > 0 ? speakers[0] : null;
      if (defaultSpeaker) {
        setSelectedSpeakerId(defaultSpeaker.id);
        setSelectedSpeakerName(defaultSpeaker.speaker_name);

        try {
          const subtitlesResp = await queryVoiceToneByRoleAndLanguageApi(defaultSpeaker.id, langCode);
          if (subtitlesResp.code === 0 && Array.isArray(subtitlesResp.data)) {
            const mappedSubtitles = subtitlesResp.data.map(item => ({
              id: item.id,
              origin_text: item.originText,
              text: item.originText,
              origin_audio: item.originAudio,
              trans_audio: '',
              origin_start_time: item.originStartTime,
              origin_end_time: item.originEndTime,
              score: item.score,
            }));
            const sortedSubtitles = mappedSubtitles.sort((a: any, b: any) => (b.score || 0) - (a.score || 0));
            setSpeakerSubtitles(sortedSubtitles);
          } else {
            setSpeakerSubtitles([]);
          }
        } catch (error) {
          console.error('获取角色字幕片段失败:', error);
          setSpeakerSubtitles([]);
        }
      } else {
        setSpeakerSubtitles([]);
      }
    } catch (error) {
      console.error('获取角色列表失败:', error);
      setSeriesSpeakers([]);
      setSelectedSpeakerId(null);
      setSelectedSpeakerName('');
      setSpeakerSubtitles([]);
    } finally {
      setLoadingSpeakers(false);
    }
  };

  // 选择角色：直接使用左侧列表中该角色携带的 subtitles（仅使用真实数据）
  const handleSpeakerSelect = async (speakerId: number, speakerName: string) => {
    setSelectedSpeakerId(speakerId);
    setSelectedSpeakerName(speakerName);
    try {
      setLoadingSubtitles(true);
      // 优先通过 /video/voice-tone/query-by-role-and-language 获取指定角色在当前语言下的字幕片段
      if (!selectedLangCode) {
        setSpeakerSubtitles([]);
        return;
      }

      const subtitlesResp = await queryVoiceToneByRoleAndLanguageApi(speakerId, selectedLangCode);
      if (subtitlesResp.code === 0 && Array.isArray(subtitlesResp.data)) {
        const mappedSubtitles = subtitlesResp.data.map(item => ({
          id: item.id,
          origin_text: item.originText,
          text: item.originText,
          origin_audio: item.originAudio,
          trans_audio: '',
          origin_start_time: item.originStartTime,
          origin_end_time: item.originEndTime,
          score: item.score,
        }));
        const sortedSubtitles = mappedSubtitles.sort((a: any, b: any) => (b.score || 0) - (a.score || 0));
        setSpeakerSubtitles(sortedSubtitles);
      } else {
        setSpeakerSubtitles([]);
      }
    } catch (error) {
      setSpeakerSubtitles([]);
    } finally {
      setLoadingSubtitles(false);
    }
  };

  // 播放音色片段
  const handlePlayClip = (subtitle: any) => {
    const audioUrl = subtitle.origin_audio || subtitle.trans_audio;
    if (!audioUrl) {
      setAlertConfig({ type: 'warning', message: '该片段暂无音频' });
      return;
    }

    // 停止当前播放的音频
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    // 如果点击的是同一个音频，则停止播放
    if (playingClipId === subtitle.id) {
      setPlayingClipId(null);
      return;
    }

    try {
      const normalizedPath = audioUrl.startsWith('/') ? audioUrl : `/${audioUrl}`;
      const fullUrl = audioUrl.startsWith('http') ? audioUrl : `${CDN_PREFIX}${normalizedPath}`;
      console.log('播放音频链接:', fullUrl);
      const audio = new Audio(fullUrl);
      audioRef.current = audio;
      
      audio.onended = () => {
        setPlayingClipId(null);
        audioRef.current = null;
      };
      
      audio.onerror = () => {
        setAlertConfig({ type: 'error', message: '音频播放失败' });
        setPlayingClipId(null);
        audioRef.current = null;
      };
      
      audio.play();
      setPlayingClipId(subtitle.id);
    } catch (error) {
      console.error('播放音频失败:', error);
      setAlertConfig({ type: 'error', message: '播放音频失败' });
      setPlayingClipId(null);
    }
  };

  // 移除音色片段
  const handleRemoveClip = async (subtitleId: number) => {
    if (!selectedSpeakerId) {
      setAlertConfig({ type: 'warning', message: '请先选择角色' });
      return;
    }
    try {
      const res = await demoteSpeakerSubtitleApi(subtitleId);
      if (res && res.code === 0) {
        // 更新右侧字幕列表
        setSpeakerSubtitles(prev => prev.filter((s: any) => s.id !== subtitleId));
        // 同步更新左侧该说话人的 subtitles
        setSeriesSpeakers(prev => prev.map(s => {
          if (s.id !== selectedSpeakerId) return s;
          const current: any = s as any;
          const subs = Array.isArray(current.subtitles) ? current.subtitles.filter((x: any) => x.id !== subtitleId) : [];
          return { ...s, subtitles: subs } as any;
        }));
        setAlertConfig({ type: 'success', message: res.msg || '已移除该音色片段' });
      } else {
        setAlertConfig({ type: 'error', message: res?.msg || '移除失败' });
      }
    } catch (e: any) {
      setAlertConfig({ type: 'error', message: e?.message || '移除失败' });
    }
  };

  // 删除角色
  const handleDeleteSpeaker = async (speakerId: number) => {
    try {
      const res: any = await deleteSpeakerApi(speakerId);
      setSeriesSpeakers(prev => prev.filter(s => s.id !== speakerId));
      // 如果当前选中被删，清空右侧
      setSpeakerSubtitles(prev => (selectedSpeakerId === speakerId ? [] : prev));
      if (selectedSpeakerId === speakerId) {
        setSelectedSpeakerId(null);
        setSelectedSpeakerName('');
      }
      setAlertConfig({ type: 'success', message: res?.message || '已删除该角色' });
    } catch (e: any) {
      setAlertConfig({ type: 'error', message: e?.message || '删除角色失败' });
    }
  };

  // 开始创建角色
  const handleStartCreateRole = () => {
    setIsCreatingRole(true);
    setNewRoleName('');
  };

  // 确认创建角色
  const handleConfirmCreateRole = async () => {
    if (!newRoleName.trim()) {
      // 如果输入为空，直接恢复到原始状态
      setIsCreatingRole(false);
      setNewRoleName('');
      return;
    }
    try {
      if (!selectedSeriesId || !selectedLangCode) {
        setAlertConfig({ type: 'error', message: '缺少剧集ID或语言代码' });
        return;
      }
      
      const params: Speakers.CreateSpeakerParams = {
        seriesId: selectedSeriesId,
        speakerName: newRoleName.trim(),
        langCode: selectedLangCode
      };
      const res = await createSpeakerApi(params);
      const created = (res as any)?.data as Speakers.Speaker | undefined;
      if (created && created.id) {
        setSeriesSpeakers(prev => [...prev, created]);
        setAlertConfig({ type: 'success', message: (res as any)?.message || `新建说话人成功：${created.speaker_name}` });
      } else {
        setAlertConfig({ type: 'error', message: '创建角色失败，请重试' });
      }
    } catch (e: any) {
      setAlertConfig({ type: 'error', message: e?.message || '创建角色失败' });
    } finally {
      setIsCreatingRole(false);
      setNewRoleName('');
    }
  };

  // 取消创建角色（点击勾选按钮恢复状态）
  const handleCancelCreateRole = () => {
    setIsCreatingRole(false);
    setNewRoleName('');
  };

  // 关闭弹窗
  const handleCloseModal = () => {
    // 停止播放的音频
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingClipId(null);
    setShowVoiceModal(false);
    setSelectedSeriesId(null);
    setSelectedSeriesName('');
    setSelectedLangCode(''); // 重置语言代码
    setSelectedSpeakerId(null);
    setSeriesSpeakers([]);
    setSpeakerSubtitles([]);
    // 重置新建角色状态
    setIsCreatingRole(false);
    setNewRoleName('');
  };

  return (
    <div className="voice-library-page">
      <AlertMessage config={alertConfig} onClose={() => setAlertConfig(null)} zIndex={ALERT_Z_INDEX.UPLOAD_VIDEO} />
      <div className="public_voices">
        <div className="public_left">
          <img src="/img/cover_white.png" alt="音色库图标" className="voice-icon" />
        </div>
        <div className="public_right">
          <div className="voice-title">公共音色库</div>
          <div className="voice-description">提供各类高质量的公共资源，助力您创作更加优质的出海视频~</div>
        </div>
      </div>

      {/* 搜索和排序区域 */}
      <div className="voice-search-filter-bar">
        <div className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {isInLanguageList && (
            <i 
              className="fas fa-chevron-left" 
              style={{ cursor: 'pointer', fontSize: '18px', color: '#666' }}
              onClick={handleBackToSeriesList}
              title="返回剧集列表"
            ></i>
          )}
          <span>{isInLanguageList ? '剧集音色库' : '剧集音色库'}</span>
        </div>
        <div className="search-sort-container">
          <div className="search-section">
            <div className="search-bar">
              <i className="fas fa-search search-icon"></i>
              <Input
                placeholder="搜索剧集"
                className="search-input show"
                value={searchInputValue}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            </div>
          </div>

          <div className="sort-section">
            <Select
              value={sortBy}
              onChange={handleSortChange}
              className="sort-dropdown"
              placeholder="排序方式"
            >
              <Select.Option value="time">按时间排序</Select.Option>
              <Select.Option value="alphabet">按名称排序</Select.Option>
            </Select>
          </div>
          {/* 团队剧集下拉选择器 - 仅管理员和超级管理员可见，且不在内层语言列表时显示 */}
          {isAdminOrSuperAdmin() && !isInLanguageList && (
            <div className="sort-section">
              <Select
                value={ownerType}
                onChange={(value) => {
                  setOwnerType(value);
                  setPage(1); // 切换时重置到第一页
                }}
                className="sort-dropdown"
                placeholder="剧集显示方式"
              >
                <Select.Option value={1}>团队剧集音色</Select.Option>
                <Select.Option value={2}>普通用户音色</Select.Option>
              </Select>
            </div>
          )}
        </div>
      </div>

      {/* 音色卡片网格 */}
      {isInLanguageList ? (
        // 内层：显示语言列表
        <>
          {loadingLanguages ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>加载中...</div>
          ) : filteredLanguageData.length === 0 ? (
            <div className="empty-search-container">
              <div className="empty-search-content">
                <div className="empty-icon">
                  <i className="fas fa-music"></i>
                </div>
                <div className="empty-text">
                  暂无语种数据
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="voices-grid">
                {pagedLanguageData.map((item) => (
                <div 
                  key={`${item.seriesId}-${item.langCode}`} 
                  className="voices-card"
                  onClick={() => {
                    // 点击语言卡片，打开音色库弹窗
                    setSelectedSeriesId(item.seriesId);
                    setSelectedSeriesName(`${item.name} — ${getLanguageName(item.langCode)}`);
                    setSelectedLangCode(item.langCode); // 保存当前选中的语言代码
                    setShowVoiceModal(true);
                    setSelectedSpeakerId(null);
                    setSelectedSpeakerName('');
                    setSpeakerSubtitles([]);
                    
                    // 获取该语言的说话人列表
                    loadSpeakersForLanguage(item.seriesId, item.langCode);
                  }}
                >
                  <div className="card-content">
                    <div className="card-left">
                      <img src="/img/cover-blue.png" alt="音色图标" className="card-icon" />
                    </div>
                    <div className="card-right">
                      <div className="card-title" title={`${item.name} — ${getLanguageName(item.langCode)}`}>
                        {item.name}
                        <span style={{ color: '#333', marginLeft: '8px' }}>
                          — {getLanguageName(item.langCode)}
                        </span>
                      </div>
                      <div className="card-subtitle">内含{item.speakerCount}个角色</div>
                    </div>
                    <div className="card-arrow">
                      <i className="fas fa-chevron-right"></i>
                    </div>
                  </div>
                </div>
              ))}
              </div>

              <MiniPaginator
                page={languagePage}
                pages={languagePages}
                hasPrev={languageHasPrev}
                hasNext={languageHasNext}
                onChange={setLanguagePage}
                className="mini-pagination-voice"
              />
            </>
          )}
        </>
      ) : (
        // 外层：显示剧集列表
        <>
          <div className="voices-grid">
            {pagedData.map((item) => (
              <div key={item.id} className="voices-card" onClick={() => handleCardClick(item.id, item.name)}>
                {item.isNew && <div className="new-badge">⚡</div>}
                <div className="card-content">
                  <div className="card-left">
                    <img src="/img/cover-blue.png" alt="音色图标" className="card-icon" />
                  </div>
                  <div className="card-right">
                    <div className="card-title" title={item.name}>{item.name}</div>
                    <div className="card-subtitle">内含{item.characterCount}个语种</div>
                  </div>
                  <div className="card-arrow">
                    <i className="fas fa-chevron-right"></i>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <MiniPaginator
            page={apiData.pagination.page}
            pages={pages}
            hasPrev={hasPrev}
            hasNext={hasNext}
            onChange={setPage}
            className="mini-pagination-voice"
          />
        </>
      )}

      {/* 剧集音色库弹窗 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img src="/img/cover-blue.png" alt="音色图标" style={{ width: '40px', height: '40px' }} />
            <span style={{ fontSize: '22px' }}>{selectedSeriesName}</span>
          </div>
        }
        open={showVoiceModal}
        onCancel={handleCloseModal}
        footer={null}
        width={900}
        className="voice-library-modal"
        style={{ top: '15%' }}
      >
        <div style={{ display: 'flex', gap: '20px', minHeight: '400px' }}>
          {/* 左侧：角色列表 */}
          <div style={{ width: '300px', borderRight: '1px solid #e0e0e0', paddingRight: '20px' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: '#333' }}>剧集音色库</h3>
            
            <div style={{ 
              display: 'flex', 
              marginBottom: '16px',
              transition: 'all 0.3s ease',
              alignItems: 'center',
              width: '100%'
            }}>
              <Input
                placeholder="请输入角色名"
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                onPressEnter={handleConfirmCreateRole}
                autoFocus={isCreatingRole}
                style={{ 
                  flex: isCreatingRole ? 1 : 0,
                  width: isCreatingRole ? 'auto' : 0,
                  opacity: isCreatingRole ? 1 : 0,
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  margin: 0,
                  marginRight: isCreatingRole ? '5px' : 0,
                  padding: isCreatingRole ? undefined : 0,
                  border: isCreatingRole ? undefined : 'none',
                  visibility: isCreatingRole ? 'visible' : 'hidden',
                  height: '40px',
                  borderRadius: 0
                }}
                className="new-role-input"
              />
              <Button
                type="primary"
                icon={isCreatingRole ? <i className="fas fa-check"></i> : <i className="fas fa-plus" style={{ marginRight: '8px' }}></i>}
                onClick={isCreatingRole ? handleConfirmCreateRole : handleStartCreateRole}
                style={{ 
                  height: '40px',
                  minWidth: isCreatingRole ? '40px' : 'auto',
                  width: isCreatingRole ? '40px' : '100%',
                  transition: 'all 0.3s ease',
                  flexShrink: 0,
                  marginLeft: 'auto',
                  borderRadius: '5px'
                }}
                className={isCreatingRole ? "confirm-create-role-btn" : "create-role-btn"}
              >
                {!isCreatingRole && '新建角色'}
              </Button>
            </div>
            
            {loadingSpeakers ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>加载中...</div>
            ) : seriesSpeakers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>暂无角色</div>
            ) : (
              <div style={{ height: '360px', overflowY: 'auto' ,marginBottom: '10px'}} className="voice-library-scroll">
                {seriesSpeakers.map((speaker) => (
                  <div
                    key={speaker.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      marginBottom: '8px',
                      border: selectedSpeakerId === speaker.id ? '1px solid #4a90e2' : '1px solid #e0e0e0',
                      borderRadius: '8px',
                      backgroundColor: selectedSpeakerId === speaker.id ? '#f0f8ff' : '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                    onClick={() => handleSpeakerSelect(speaker.id, speaker.speaker_name)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                      <i className="fas fa-music" style={{ color: '#4a90e2', fontSize: '16px' }}></i>
                      <span style={{ fontSize: '14px', color: '#333', fontWeight: '500' }}>{speaker.speaker_name}</span>
                    </div>
                    <img 
                      src="/img/del.png" 
                      alt="删除"
                      style={{ width: '27px', height: '27px', cursor: 'pointer', padding: '4px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSpeaker(speaker.id);
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 右侧：音色片段列表 */}
          <div style={{ flex: 1, paddingLeft: '20px' }}>
            {selectedSpeakerId ? (
              <>
                <div style={{ marginBottom: '16px', fontSize: '14px', color: '#666', fontWeight: 500 }}>
                  {selectedSpeakerName}的音色
                </div>
                
                {loadingSubtitles ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>加载中...</div>
                ) : speakerSubtitles.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>暂无音色片段</div>
                ) : (
                  <div style={{ height: '420px', overflowY: 'auto',marginBottom: '10px' }} className="voice-library-scroll">
                    {speakerSubtitles.map((subtitle: any) => (
                      <div
                        key={subtitle.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px 16px',
                          marginBottom: '12px',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          backgroundColor: '#fff',
                        }}
                      >
                        <span style={{ fontSize: '14px', color: '#333', flex: 1 }}>{subtitle.origin_text || subtitle.text}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <button
                            onClick={() => handlePlayClip(subtitle)}
                            style={{
                              width: '24px',
                              height: '24px',
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer',
                              padding: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {playingClipId === subtitle.id ? (
                              <i className="fas fa-pause" style={{ color: '#000', fontSize: '14px' }}></i>
                            ) : (
                              <i className="fas fa-play" style={{ color: '#000', fontSize: '14px' }}></i>
                            )}
                          </button>
                          <Button
                            size="small"
                            danger
                            onClick={() => handleRemoveClip(subtitle.id)}
                          >
                            移除
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                请选择左侧角色查看音色片段
              </div>
            )}
          </div>
        </div>
      </Modal>

      <CopyrightFooter />
    </div>
  )
};
